import { useCallback, useMemo, useRef } from 'react';

import { GAME_CONFIG, BIOMES, BiomeConfig } from './constants';
import { RabbitEntity, WormEntity } from './entities/animals';
import { Entity } from './entities/Entity';
import { worldLogger } from './Logger';

// Seeded random number generator return type
export type SeededRandomFunction = () => number;

// Pseudo-random number generator with seed
export const seededRandom = (seed: number): SeededRandomFunction => {
  let state = seed;
  return (): number => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
};

// Perlin noise implementation
class PerlinNoise {
  private seed: number;
  private permutation: number[];
  private p: number[];

  constructor(seed: number = 12345) {
    this.seed = seed;
    this.permutation = this.generatePermutation();
    this.p = [...this.permutation, ...this.permutation]; // Duplicate for overflow
  }

  private generatePermutation(): number[] {
    const rng = seededRandom(this.seed);
    const p: number[] = [];
    for (let i = 0; i < 256; i++) {
      p[i] = i;
    }
    // Shuffle using seeded random
    for (let i = 255; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      // Safe array access - both i and j are within bounds [0, 255]
      const temp = p[i] ?? 0;
      p[i] = p[j] ?? 0;
      p[j] = temp;
    }
    return p;
  }

  private fade(t: number): number {
    return t * t * t * (t * (t * 6 - 15) + 10);
  }

  private lerp(t: number, a: number, b: number): number {
    return a + t * (b - a);
  }

  private grad(hash: number, x: number, y: number): number {
    const h = hash & 15;
    const u = h < 8 ? x : y;
    const v = h < 4 ? y : h === 12 || h === 14 ? x : 0;
    return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
  }

  public noise(x: number, y: number): number {
    const X = Math.floor(x) & 255;
    const Y = Math.floor(y) & 255;

    x -= Math.floor(x);
    y -= Math.floor(y);

    const u = this.fade(x);
    const v = this.fade(y);

    const A = (this.p[X] ?? 0) + Y;
    const AA = this.p[A] ?? 0;
    const AB = this.p[A + 1] ?? 0;
    const B = (this.p[X + 1] ?? 0) + Y;
    const BA = this.p[B] ?? 0;
    const BB = this.p[B + 1] ?? 0;

    return this.lerp(
      v,
      this.lerp(
        u,
        this.grad(this.p[AA] ?? 0, x, y),
        this.grad(this.p[BA] ?? 0, x - 1, y)
      ),
      this.lerp(
        u,
        this.grad(this.p[AB] ?? 0, x, y - 1),
        this.grad(this.p[BB] ?? 0, x - 1, y - 1)
      )
    );
  }

  // Multi-octave noise for more natural patterns
  public fbm(
    x: number,
    y: number,
    octaves: number = 4,
    frequency: number = 0.01,
    amplitude: number = 1,
    lacunarity: number = 2,
    persistence: number = 0.5
  ): number {
    let value = 0;
    let maxValue = 0;

    for (let i = 0; i < octaves; i++) {
      value += this.noise(x * frequency, y * frequency) * amplitude;
      maxValue += amplitude;
      frequency *= lacunarity;
      amplitude *= persistence;
    }

    return value / maxValue;
  }
}

// Global noise instance
const perlin = new PerlinNoise(GAME_CONFIG.WORLD_SEED);

const BIOME_DOMAIN_WARP_FREQUENCY = 0.0075;
const BIOME_DOMAIN_WARP_STRENGTH = 42;
const BIOME_ELEVATION_FREQUENCY = 0.0062;
const BIOME_TEMPERATURE_FREQUENCY = 0.0048;
const BIOME_MOISTURE_FREQUENCY = 0.0052;
const BIOME_RUGGEDNESS_FREQUENCY = 0.0086;
const BIOME_BASIN_FREQUENCY = 0.0046;
const STARTING_BIOME_TEMPER_RADIUS = 40;
const STARTING_BIOME_MOISTURE_TARGET = 0.05;

const getSafeStartingTerrainChar = (biome: BiomeConfig): string => {
  if (
    biome.terrainChar === GAME_CONFIG.SAND_CHARACTER ||
    biome.name === 'Badlands'
  ) {
    return GAME_CONFIG.SAND_CHARACTER;
  }

  return GAME_CONFIG.GRASS_CHARACTER;
};

// Determine biome at coordinates using Perlin noise
export const getBiome = (x: number, y: number): BiomeConfig => {
  const centerX = GAME_CONFIG.WORLD_WIDTH / 2;
  const centerY = GAME_CONFIG.WORLD_HEIGHT / 2;

  // Spawn safety is handled in generateTile; biome selection stays consistent
  // across the whole world so the starting area can inherit any biome.
  const warpX =
    perlin.fbm(x + 6100, y + 6100, 3, BIOME_DOMAIN_WARP_FREQUENCY, 1, 2, 0.55) *
    BIOME_DOMAIN_WARP_STRENGTH;
  const warpY =
    perlin.fbm(x + 7300, y + 7300, 3, BIOME_DOMAIN_WARP_FREQUENCY, 1, 2, 0.55) *
    BIOME_DOMAIN_WARP_STRENGTH;
  const sampleX = x + warpX;
  const sampleY = y + warpY;

  // Higher sampling frequencies produce tighter biome regions while the warp
  // avoids obvious straight contour bands.
  const biomeNoise = perlin.fbm(
    sampleX,
    sampleY,
    4,
    BIOME_ELEVATION_FREQUENCY,
    1,
    2,
    0.52
  );
  const temperatureNoise = perlin.fbm(
    sampleX + 1000,
    sampleY + 1000,
    3,
    BIOME_TEMPERATURE_FREQUENCY,
    1,
    2,
    0.58
  );
  const moistureNoise = perlin.fbm(
    sampleX + 2000,
    sampleY + 2000,
    3,
    BIOME_MOISTURE_FREQUENCY,
    1,
    2,
    0.58
  );
  const ruggednessNoise = perlin.fbm(
    sampleX + 3200,
    sampleY + 3200,
    3,
    BIOME_RUGGEDNESS_FREQUENCY,
    1,
    2,
    0.55
  );
  const basinNoise = perlin.fbm(
    sampleX + 4100,
    sampleY + 4100,
    3,
    BIOME_BASIN_FREQUENCY,
    1,
    2,
    0.58
  );

  // Keep the spawn region biome-flexible but temper extremes so the start does
  // not land in a huge desert, lake, or mountain pocket. This is a bias, not a
  // hard biome override.
  const distanceFromCenter = Math.max(
    Math.abs(x - centerX),
    Math.abs(y - centerY)
  );
  const spawnBlend = Math.max(
    0,
    1 - distanceFromCenter / STARTING_BIOME_TEMPER_RADIUS
  );
  const spawnBias = spawnBlend * spawnBlend * (3 - 2 * spawnBlend);

  // Combine noise for biome selection.
  let temperature = temperatureNoise; // -1 to 1 (cold to hot)
  let moisture = moistureNoise; // -1 to 1 (dry to wet)
  let elevation = biomeNoise; // -1 to 1 (low to high)
  let ruggedness = ruggednessNoise; // -1 to 1 (smooth to broken)
  let basin = basinNoise; // -1 to 1 (raised to lowland)

  if (spawnBias > 0) {
    temperature *= 1 - spawnBias * 0.55;
    moisture += (STARTING_BIOME_MOISTURE_TARGET - moisture) * spawnBias * 0.7;
    elevation *= 1 - spawnBias * 0.16;
    ruggedness *= 1 - spawnBias * 0.22;
    basin *= 1 - spawnBias * 0.18;
  }

  // Biome selection based on temperature, moisture, and elevation.
  // Plains is intentionally a narrow temperate middle band instead of the
  // default fallback so the more distinctive biomes claim more territory.
  if (elevation > 0.5 || (elevation > 0.36 && ruggedness > 0.16)) {
    return BIOMES.MOUNTAINS;
  }
  if (
    elevation < -0.48 ||
    (moisture > 0.46 && basin < -0.08 && elevation < 0.08)
  ) {
    return BIOMES.LAKE;
  }
  if (moisture > 0.34 && basin < 0 && elevation < 0.18) {
    return BIOMES.WETLANDS;
  }
  if (temperature < -0.14 && (elevation > 0.02 || moisture < 0.24)) {
    return BIOMES.TUNDRA;
  }
  if (temperature > 0.18 && moisture < -0.12 && ruggedness > 0.04) {
    return BIOMES.BADLANDS;
  }
  if (moisture > 0.16 && temperature > -0.28) return BIOMES.FOREST;
  if (moisture < -0.14 && temperature > 0.02) return BIOMES.DESERT;

  const isTemperatePlainsBand =
    Math.abs(temperature) < 0.12 &&
    Math.abs(moisture) < 0.1 &&
    elevation > -0.08 &&
    elevation < 0.16 &&
    ruggedness < 0.14 &&
    basin > -0.18 &&
    basin < 0.2;
  if (isTemperatePlainsBand) {
    return BIOMES.PLAINS;
  }

  if (moisture >= 0.1) {
    return elevation < 0.05 && basin < -0.05 ? BIOMES.WETLANDS : BIOMES.FOREST;
  }
  if (temperature <= -0.08) {
    return BIOMES.TUNDRA;
  }
  if (elevation < -0.22 && basin < 0.08) {
    return BIOMES.LAKE;
  }
  if (ruggedness > 0.18 && temperature > 0.08) {
    return BIOMES.BADLANDS;
  }
  if (temperature > 0.05 || moisture < -0.08) {
    return BIOMES.DESERT;
  }
  return BIOMES.PLAINS;
};

// Check if position is near a river using Perlin noise
export const isRiver = (x: number, y: number): boolean => {
  // Create winding rivers using Perlin noise
  const riverNoise = perlin.fbm(x, y + 5000, 2, 0.008, 1, 2, 0.6);
  const riverWidth = perlin.noise(x * 0.005, y * 0.005) * 0.05 + 0.08;

  // Rivers follow valleys in the noise
  const hasRiver = Math.abs(riverNoise) < riverWidth;

  // Debug logging for rivers (sample every 50th position to avoid spam)
  if (hasRiver && x % 50 === 0 && y % 50 === 0) {
    worldLogger.debug('River generated at coordinates', {
      position: { x, y },
      riverNoise: riverNoise.toFixed(3),
      riverWidth: riverWidth.toFixed(3),
    });
  }

  return hasRiver;
};

// Check if position should have a land bridge over rivers
export const isLandBridge = (x: number, y: number): boolean => {
  // Only generate bridges where there are rivers
  if (!isRiver(x, y)) return false;

  // Check if this position is near any bridge center
  const bridgeRadius = 4; // Create larger 9x9 bridge clusters

  for (let dx = -bridgeRadius; dx <= bridgeRadius; dx++) {
    for (let dy = -bridgeRadius; dy <= bridgeRadius; dy++) {
      const centerX = x + dx;
      const centerY = y + dy;

      // Check if this could be a bridge center using noise
      const bridgeNoise = perlin.fbm(
        centerX + 15000,
        centerY + 15000,
        3,
        0.015,
        1,
        2,
        0.5
      );
      const isBridgeCenter = bridgeNoise > 0.15; // Lower threshold for more frequent bridge centers

      if (isBridgeCenter) {
        // Check if current position is within bridge radius of this center
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance <= bridgeRadius) {
          // Debug logging for bridge generation (only log once per center)
          if (dx === 0 && dy === 0) {
            worldLogger.debug('Land bridge cluster generated', {
              center: { x: centerX, y: centerY },
              bridgeNoise: bridgeNoise.toFixed(3),
            });
          }
          return true;
        }
      }
    }
  }

  return false;
};

// Check if position is in a tree cluster using Perlin noise
export const getTreeClusterDensity = (
  x: number,
  y: number,
  biome: BiomeConfig
): number => {
  // No trees in desert
  if (biome.name === 'Desert') return 0;

  // Use Perlin noise to create natural tree clusters
  const clusterNoise = perlin.fbm(x, y + 3000, 3, 0.08, 1, 2, 0.5); // Higher frequency for smaller clusters
  const detailNoise = perlin.noise(x * 0.15, y * 0.15) * 0.3; // Fine detail for natural edges

  const combinedNoise = clusterNoise + detailNoise;

  // Biome-specific tree cluster thresholds and densities
  let threshold: number, maxDensity: number;
  switch (biome.name) {
    case 'Forest':
      threshold = 0.2;
      maxDensity = 0.7;
      break;
    case 'Wetlands':
      threshold = 0.32;
      maxDensity = 0.62;
      break;
    case 'Plains':
      threshold = 0.5;
      maxDensity = 0.4;
      break;
    case 'Tundra':
      threshold = 0.78;
      maxDensity = 0.08;
      break;
    case 'Mountains':
      threshold = 0.6;
      maxDensity = 0.3;
      break;
    case 'Lake':
      threshold = 0.4;
      maxDensity = 0.5;
      break;
    case 'Badlands':
      threshold = 0.86;
      maxDensity = 0.05;
      break;
    default:
      return 0;
  }

  if (combinedNoise > threshold) {
    // Smooth falloff at cluster edges
    const intensity = Math.min(
      1,
      (combinedNoise - threshold) / (1 - threshold)
    );
    return intensity * maxDensity;
  }

  return 0;
};

// Check if position is in a rock cluster using Perlin noise
export const getRockClusterDensity = (
  x: number,
  y: number,
  biome: BiomeConfig
): number => {
  // No rocks in lake biomes
  if (biome.name === 'Lake') return 0;

  // Use different noise offset for rocks to avoid overlapping with trees
  const clusterNoise = perlin.fbm(x + 10000, y + 10000, 3, 0.09, 1, 2, 0.5);
  const detailNoise = perlin.noise(x * 0.12, y * 0.12) * 0.25;

  const combinedNoise = clusterNoise + detailNoise;

  // Biome-specific rock cluster thresholds and densities
  let threshold: number, maxDensity: number;
  switch (biome.name) {
    case 'Mountains':
      threshold = 0.15;
      maxDensity = 0.6;
      break;
    case 'Badlands':
      threshold = 0.2;
      maxDensity = 0.55;
      break;
    case 'Desert':
      threshold = 0.3;
      maxDensity = 0.4;
      break;
    case 'Tundra':
      threshold = 0.42;
      maxDensity = 0.34;
      break;
    case 'Plains':
      threshold = 0.6;
      maxDensity = 0.25;
      break;
    case 'Forest':
      threshold = 0.7;
      maxDensity = 0.2;
      break;
    case 'Wetlands':
      threshold = 0.76;
      maxDensity = 0.14;
      break;
    default:
      return 0;
  }

  if (combinedNoise > threshold) {
    // Smooth falloff at cluster edges
    const intensity = Math.min(
      1,
      (combinedNoise - threshold) / (1 - threshold)
    );
    return intensity * maxDensity;
  }

  return 0;
};

// Resource node type
export type ResourceNodeType = 'iron' | 'copper';

// Check if position should have a resource node (iron/copper) in small clumps
export const getResourceNodeCluster = (
  x: number,
  y: number,
  biome: BiomeConfig,
  nodeType: ResourceNodeType
): number => {
  const baseRate = nodeType === 'iron' ? biome.ironRate : biome.copperRate;

  if (baseRate === 0) return 0;

  // Use noise to create clusters - simpler approach
  const clusterNoise = perlin.fbm(
    x + (nodeType === 'iron' ? 20000 : 30000),
    y + (nodeType === 'iron' ? 20000 : 30000),
    2,
    0.05,
    1,
    2,
    0.6
  );

  // Simple threshold approach - if above threshold, use base rate multiplied by cluster density
  let clusterThreshold: number;
  let clusterMultiplier: number;

  switch (biome.name) {
    case 'Mountains':
      clusterThreshold = 0.3;
      clusterMultiplier = 8; // Strong clusters in mountains
      break;
    case 'Badlands':
      clusterThreshold = 0.34;
      clusterMultiplier = 7;
      break;
    case 'Desert':
      clusterThreshold = 0.4;
      clusterMultiplier = 6;
      break;
    case 'Tundra':
      clusterThreshold = 0.48;
      clusterMultiplier = 6;
      break;
    case 'Plains':
      clusterThreshold = 0.5;
      clusterMultiplier = 5;
      break;
    case 'Forest':
      clusterThreshold = 0.6;
      clusterMultiplier = 4;
      break;
    case 'Wetlands':
      clusterThreshold = 0.64;
      clusterMultiplier = 4;
      break;
    case 'Lake':
      clusterThreshold = 0.7;
      clusterMultiplier = 3;
      break;
    default:
      return 0;
  }

  if (clusterNoise > clusterThreshold) {
    // Return higher probability within clusters
    return Math.min(0.4, baseRate * clusterMultiplier);
  }

  return 0;
};

// Generate world tile at specific coordinates
export const generateTile = (x: number, y: number): string => {
  const rng = seededRandom(GAME_CONFIG.WORLD_SEED + x * 1000 + y);

  // Border walls
  if (
    x === 0 ||
    x === GAME_CONFIG.WORLD_WIDTH - 1 ||
    y === 0 ||
    y === GAME_CONFIG.WORLD_HEIGHT - 1
  ) {
    return GAME_CONFIG.WALL_CHARACTER;
  }

  const centerX = GAME_CONFIG.WORLD_WIDTH / 2;
  const centerY = GAME_CONFIG.WORLD_HEIGHT / 2;
  const distanceFromCenter = Math.max(
    Math.abs(x - centerX),
    Math.abs(y - centerY)
  );

  const biome = getBiome(x, y);

  // Generate random values for this tile
  const rand1 = rng();
  const rand2 = rng();
  const rand3 = rng();
  const rand4 = rng();
  const rand6 = rng();
  const rand7 = rng();
  const rand8 = rng();

  // Safe starting zone - no obstacles except resources and grass
  if (distanceFromCenter < 15) {
    const safeTerrainChar = getSafeStartingTerrainChar(biome);

    // Very rare chance of additional resources (we have guaranteed ones)
    if (rand1 < 0.002) {
      // 0.2% chance
      return GAME_CONFIG.IRON_NODE_CHARACTER;
    } else if (rand2 < 0.002) {
      // 0.2% chance
      return GAME_CONFIG.COPPER_NODE_CHARACTER;
    } else if (rand4 < 0.15) {
      return safeTerrainChar;
    }
    return GAME_CONFIG.EMPTY_CHARACTER;
  }

  // Transition zone - gradual introduction of obstacles
  if (distanceFromCenter < 35) {
    const transitionFactor = (distanceFromCenter - 15) / 20.0; // 0 to 1

    // Only allow light obstacles in transition zone
    if (rand2 < getTreeClusterDensity(x, y, biome) * transitionFactor * 0.5) {
      return GAME_CONFIG.TREE_CHARACTER;
    } else if (
      rand3 <
      getRockClusterDensity(x, y, biome) * transitionFactor * 0.3
    ) {
      return GAME_CONFIG.ROCK_CHARACTER;
    } else if (rand4 < (biome.grassRate || biome.sandRate || 0)) {
      return biome.terrainChar;
    }
    return GAME_CONFIG.EMPTY_CHARACTER;
  }

  // Full biome generation outside transition zone
  // Check for rivers first (in appropriate biomes), but override with land bridges
  if (
    (biome.name === 'Lake' ||
      biome.name === 'Wetlands' ||
      biome.name === 'Plains' ||
      biome.name === 'Forest') &&
    isRiver(x, y)
  ) {
    // Check if there should be a land bridge here
    if (isLandBridge(x, y)) {
      // Land bridge - generate normal terrain as if there was no river
      // Continue to the normal terrain generation below instead of returning water
    } else {
      return GAME_CONFIG.WATER_CHARACTER;
    }
  }

  // Tree clustering logic using Perlin noise density
  const treeDensity = getTreeClusterDensity(x, y, biome);
  if (treeDensity > 0 && rand1 < treeDensity) {
    return GAME_CONFIG.TREE_CHARACTER;
  }

  // Rock clustering logic using Perlin noise density
  const rockDensity = getRockClusterDensity(x, y, biome);
  if (rockDensity > 0 && rand2 < rockDensity) {
    return GAME_CONFIG.ROCK_CHARACTER;
  }

  // Resource node clustering logic - iron and copper in small clumps
  const ironClusterDensity = getResourceNodeCluster(x, y, biome, 'iron');
  if (ironClusterDensity > 0 && rand3 < ironClusterDensity) {
    return GAME_CONFIG.IRON_NODE_CHARACTER;
  }

  const copperClusterDensity = getResourceNodeCluster(x, y, biome, 'copper');
  if (copperClusterDensity > 0 && rand4 < copperClusterDensity) {
    return GAME_CONFIG.COPPER_NODE_CHARACTER;
  }

  // Regular resources (the * character) - removed
  if (
    rand6 < (biome.waterRate || 0) &&
    (biome.name === 'Lake' || biome.name === 'Wetlands')
  ) {
    return GAME_CONFIG.WATER_CHARACTER;
  } else if (rand7 < (biome.mountainRate || 0)) {
    return GAME_CONFIG.MOUNTAIN_CHARACTER;
  } else if (rand8 < (biome.grassRate || biome.sandRate || 0)) {
    return biome.terrainChar;
  }

  return GAME_CONFIG.EMPTY_CHARACTER;
};

// World cache type
export type WorldCache = Record<string, string>;

// Game map type (imported from GameState)
export type GameMap = Record<string, string>;

// Building layer type (imported from GameState)
export type BuildingLayer = Record<string, string>;

// World generation hook return type
export interface UseWorldGenerationReturn {
  getTile: (
    x: number,
    y: number,
    gameMap: GameMap,
    buildingLayer?: BuildingLayer
  ) => string | undefined;
  getOriginalTile: (x: number, y: number) => string | undefined;
  preGenerateArea: (centerX: number, centerY: number, radius?: number) => void;
  initializeWorld: () => void;
  worldCache: WorldCache;
  // Utility functions
  getBiome: (x: number, y: number) => BiomeConfig;
  isRiver: (x: number, y: number) => boolean;
  isLandBridge: (x: number, y: number) => boolean;
  getTreeClusterDensity: (x: number, y: number, biome: BiomeConfig) => number;
  getRockClusterDensity: (x: number, y: number, biome: BiomeConfig) => number;
  getResourceNodeCluster: (
    x: number,
    y: number,
    biome: BiomeConfig,
    nodeType: ResourceNodeType
  ) => number;
  perlin: PerlinNoise; // Export for debugging
}

// World generation hook for managing world cache and tile lookup
export const useWorldGeneration = (): UseWorldGenerationReturn => {
  const worldCache = useRef<WorldCache>({});

  // Fast tile lookup with caching and layered rendering
  const getTile = useCallback(
    (
      x: number,
      y: number,
      gameMap: GameMap,
      buildingLayer?: BuildingLayer
    ): string | undefined => {
      const key = `${x},${y}`;

      // Check building layer first (buildings are rendered on top)
      if (buildingLayer && key in buildingLayer) {
        return buildingLayer[key];
      }

      // Check terrain modifications next (for collected resources)
      if (key in gameMap) {
        return gameMap[key];
      }

      // Check cache for world-generated terrain
      if (!(key in worldCache.current)) {
        worldCache.current[key] = generateTile(x, y);
      }

      return worldCache.current[key];
    },
    []
  );

  // Pre-generate tiles around a position with performance optimizations
  const preGenerateArea = useCallback(
    (centerX: number, centerY: number, radius: number = 25): void => {
      // Reduce default radius from 80 to 25 for better performance
      // This generates (25*2+1)^2 = 2,601 tiles instead of (80*2+1)^2 = 25,921

      let tilesGenerated = 0;
      const maxTilesPerCall = 500; // Limit tiles generated per call to prevent lag

      for (
        let y = centerY - radius;
        y <= centerY + radius && tilesGenerated < maxTilesPerCall;
        y++
      ) {
        for (
          let x = centerX - radius;
          x <= centerX + radius && tilesGenerated < maxTilesPerCall;
          x++
        ) {
          if (
            x >= 0 &&
            x < GAME_CONFIG.WORLD_WIDTH &&
            y >= 0 &&
            y < GAME_CONFIG.WORLD_HEIGHT
          ) {
            const key = `${x},${y}`;
            if (!(key in worldCache.current)) {
              worldCache.current[key] = generateTile(x, y);
              tilesGenerated++;
            }
          }
        }
      }

      // Log performance metrics for debugging
      if (tilesGenerated > 0) {
        worldLogger.debug('Pre-generated tiles for movement', {
          centerX,
          centerY,
          radius,
          tilesGenerated,
          cacheSize: Object.keys(worldCache.current).length,
        });
      }
    },
    []
  );

  // Ensure starting area has one of each resource type
  const ensureStartingResources = useCallback(
    (centerX: number, centerY: number): void => {
      const resourceTypes = [
        { char: GAME_CONFIG.IRON_NODE_CHARACTER, name: 'Iron' },
        { char: GAME_CONFIG.COPPER_NODE_CHARACTER, name: 'Copper' },
      ];

      // Force placement of each resource type in starting area
      // Special seed for guaranteed resources

      // Place each resource type at a guaranteed position
      resourceTypes.forEach((resource, index) => {
        // Calculate position in a triangle pattern around spawn
        const angle = (index * 2 * Math.PI) / 3; // 120 degrees apart
        const distance = 8; // Fixed distance from center
        const x = Math.floor(centerX + Math.cos(angle) * distance);
        const y = Math.floor(centerY + Math.sin(angle) * distance);
        const key = `${x},${y}`;

        // Force place the resource regardless of what's there
        worldCache.current[key] = resource.char;
        worldLogger.info(
          `Placed guaranteed ${resource.name} at (${x}, ${y}) in starting area`
        );
      });
    },
    []
  );

  // Initialize world around spawn point
  const initializeWorld = useCallback((): void => {
    const startX = Math.floor(GAME_CONFIG.WORLD_WIDTH / 2);
    const startY = Math.floor(GAME_CONFIG.WORLD_HEIGHT / 2);

    // Pre-generate a large area around spawn
    preGenerateArea(startX, startY, 100);

    // Ensure one of each resource type in starting area
    ensureStartingResources(startX, startY);
  }, [ensureStartingResources, preGenerateArea]);

  // Get the original world-generated tile (ignoring game map modifications)
  const getOriginalTile = useCallback(
    (x: number, y: number): string | undefined => {
      // Check bounds
      if (
        x < 0 ||
        x >= GAME_CONFIG.WORLD_WIDTH ||
        y < 0 ||
        y >= GAME_CONFIG.WORLD_HEIGHT
      ) {
        return undefined;
      }

      const key = `${x},${y}`;

      // Check cache first
      if (!(key in worldCache.current)) {
        worldCache.current[key] = generateTile(x, y);
      }

      return worldCache.current[key];
    },
    []
  );

  return useMemo(
    () => ({
      getTile,
      getOriginalTile,
      preGenerateArea,
      initializeWorld,
      worldCache: worldCache.current,
      // Utility functions
      getBiome,
      isRiver,
      isLandBridge,
      getTreeClusterDensity,
      getRockClusterDensity,
      getResourceNodeCluster,
      perlin, // Export for debugging
    }),
    [getOriginalTile, getTile, initializeWorld, preGenerateArea]
  );
};

// Generate entities for a specific world position
export const generateEntitiesForPosition = (
  x: number,
  y: number,
  entityIdPrefix: string = 'wildlife'
): Entity[] => {
  const entities: Entity[] = [];

  if (
    x < 0 ||
    y < 0 ||
    x >= GAME_CONFIG.WORLD_WIDTH ||
    y >= GAME_CONFIG.WORLD_HEIGHT
  ) {
    return entities;
  }

  const rngSeed =
    GAME_CONFIG.WORLD_SEED * 7919 +
    x * 29791 +
    y * 101111 +
    entityIdPrefix.length * 379;
  const rng = seededRandom(Math.floor(Math.abs(rngSeed)));

  const rabbitRoll = rng();
  const rabbitStateRoll = rng();
  const wormRoll = rng();
  const wormHiddenRoll = rng();
  const wormTimerRoll = rng();

  const tile = generateTile(x, y);
  const biome = getBiome(x, y);
  const nearRiver = isRiver(x, y);

  const isWater = tile === GAME_CONFIG.WATER_CHARACTER;
  const isBlockingTile =
    tile === GAME_CONFIG.WALL_CHARACTER ||
    tile === GAME_CONFIG.TREE_CHARACTER ||
    tile === GAME_CONFIG.ROCK_CHARACTER ||
    tile === GAME_CONFIG.MOUNTAIN_CHARACTER;

  if (isWater || isBlockingTile) {
    return entities;
  }

  if (tile === GAME_CONFIG.GRASS_CHARACTER) {
    const baseRabbitChance = 0.0035; // 0.35%
    let biomeModifier = 1;

    switch (biome.name) {
      case 'Plains':
        biomeModifier = 1.6;
        break;
      case 'Forest':
        biomeModifier = 1.25;
        break;
      case 'Wetlands':
        biomeModifier = 1.1;
        break;
      case 'Tundra':
        biomeModifier = 0.55;
        break;
      case 'Mountains':
        biomeModifier = 0.4;
        break;
      case 'Badlands':
        biomeModifier = 0.45;
        break;
      case 'Desert':
        biomeModifier = 0.2;
        break;
      case 'Lake':
        biomeModifier = 0.9;
        break;
      default:
        biomeModifier = 1;
        break;
    }

    const rabbitChance = baseRabbitChance * biomeModifier;
    if (rabbitRoll < rabbitChance) {
      const rabbit = new RabbitEntity(
        `${entityIdPrefix}_rabbit_${x}_${y}`,
        { x, y },
        { startleOnSpawn: rabbitStateRoll > 0.85 }
      );
      entities.push(rabbit);
    }
  }

  const canSpawnWorm =
    tile === GAME_CONFIG.SAND_CHARACTER ||
    (tile === GAME_CONFIG.GRASS_CHARACTER && nearRiver);

  if (canSpawnWorm) {
    const baseWormChance = 0.0025;
    let biomeModifier = 1;

    switch (biome.name) {
      case 'Lake':
        biomeModifier = 1.8;
        break;
      case 'Wetlands':
        biomeModifier = 1.55;
        break;
      case 'Forest':
        biomeModifier = 1.3;
        break;
      case 'Plains':
        biomeModifier = 1;
        break;
      case 'Badlands':
        biomeModifier = 0.8;
        break;
      case 'Desert':
        biomeModifier = 0.3;
        break;
      case 'Tundra':
        biomeModifier = 0.4;
        break;
      case 'Mountains':
        biomeModifier = 0.5;
        break;
      default:
        biomeModifier = 1;
        break;
    }

    const riverBonus = nearRiver ? 0.0015 : 0;
    const wormChance = Math.max(0, baseWormChance * biomeModifier + riverBonus);

    if (wormRoll < wormChance) {
      const startHidden = wormHiddenRoll > 0.55;
      const initialTimer = 1400 + wormTimerRoll * 2400;
      const worm = new WormEntity(
        `${entityIdPrefix}_worm_${x}_${y}`,
        { x, y },
        {
          startHidden,
          initialSurfaceTimerMs: initialTimer,
        }
      );
      entities.push(worm);
    }
  }

  return entities;
};

// Get modified terrain tile that doesn't include resources (since they're now entities)
export const generateTerrainTile = (x: number, y: number): string => {
  return generateTile(x, y);
};
